// src/components/auth/ChangePasswordModal.tsx

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { KeyRound } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { authService } from 'services/authService';
import { logout } from 'store/slices/authSlice';
import { showToast } from 'store/slices/toastSlice';

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Required'),
    newPassword:     z.string().min(6, 'Min 6 characters'),
    confirmPassword: z.string().min(1, 'Required'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: "Passwords don't match",
    path:    ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

interface Props {
  onClose: () => void;
  // Forced first-login reset (see mustResetPassword) — no dismiss, explains why.
  mandatory?: boolean;
}

export default function ChangePasswordModal({ onClose, mandatory = false }: Props) {
  const dispatch = useDispatch();
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  async function onSubmit(data: FormData) {
    setApiError('');
    try {
      await authService.changePassword(data.currentPassword, data.newPassword);
      dispatch(showToast('✅ Password changed — please login again'));
      dispatch(logout());
    } catch (err: any) {
      setApiError(err?.response?.data?.message || 'Failed to change password');
    }
  }

  const inp = (hasError?: boolean) =>
    `w-full px-3 py-2 bg-[var(--om-panel)] border rounded-lg ` +
    `text-[var(--om-text)] text-xs outline-none focus:border-blue-500 placeholder:text-[var(--om-text-dim)] ${
      hasError ? 'border-red-500' : 'border-[var(--om-border)]'
    }`;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 z-[90]"
        onClick={mandatory ? undefined : onClose}
      />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100]
                      w-full max-w-sm bg-[var(--om-panel)] border border-[var(--om-border)] rounded-2xl
                      p-6 shadow-2xl">
        <h2 className="inline-flex items-center gap-1.5 text-sm font-black text-[var(--om-text)] mb-1"><KeyRound className="w-4 h-4" strokeWidth={2} /> Change Password</h2>
        {mandatory && (
          <p className="text-[11px] text-amber-400 mb-3">
            Your account was just created — set a new password to continue.
          </p>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <div>
            <label className="text-[11px] text-[var(--om-text-dim)] font-bold uppercase tracking-widest block mb-1">
              Current Password
            </label>
            <input {...register('currentPassword')} type="password" className={inp(!!errors.currentPassword)} placeholder="••••••••" />
            {errors.currentPassword && <p className="text-red-400 text-[11px] mt-0.5">{errors.currentPassword.message}</p>}
          </div>

          <div>
            <label className="text-[11px] text-[var(--om-text-dim)] font-bold uppercase tracking-widest block mb-1">
              New Password
            </label>
            <input {...register('newPassword')} type="password" className={inp(!!errors.newPassword)} placeholder="Min 6 characters" />
            {errors.newPassword && <p className="text-red-400 text-[11px] mt-0.5">{errors.newPassword.message}</p>}
          </div>

          <div>
            <label className="text-[11px] text-[var(--om-text-dim)] font-bold uppercase tracking-widest block mb-1">
              Confirm New Password
            </label>
            <input {...register('confirmPassword')} type="password" className={inp(!!errors.confirmPassword)} placeholder="Re-enter new password" />
            {errors.confirmPassword && <p className="text-red-400 text-[11px] mt-0.5">{errors.confirmPassword.message}</p>}
          </div>

          {apiError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-red-400 text-[11px]">
              {apiError}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            {!mandatory && (
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 bg-[var(--om-card)] border border-[var(--om-border)] rounded-xl text-[var(--om-text-muted)] text-xs hover:text-[var(--om-text)] transition-all"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-[var(--om-border)] disabled:text-[var(--om-text-dim)] text-white rounded-xl font-bold text-xs transition-all"
            >
              {isSubmitting ? 'Saving...' : 'Change Password'}
            </button>
          </div>
        </form>

        <p className="text-[11px] text-[var(--om-text-dim)] text-center mt-3">
          All active sessions will be logged out after changing password
        </p>
      </div>
    </>
  );
}
